import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { InstagramEmbed } from "@/components/media/InstagramEmbed";
import PersonalCTA from "@/components/home/PersonalCTA";
import Contact from "@/components/site/Contact";

const CaseStudyPage = ({ study }) => {
  const crumbs = [{ name: "Home", to: "/" }, { name: "Our Work", to: "/work" }, { name: study.client, to: study.path }];
  return (
    <article data-testid={`case-study-${study.slug}`}>
      <Seo title={study.title} description={study.description} schema={[breadcrumbSchema(crumbs)]} />

      <header className="pt-32 md:pt-44 pb-12 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <Breadcrumbs items={crumbs} />
          <p className="mt-10 label-eyebrow text-[#38BDF8]">{study.eyebrow}</p>
          <h1 className="mt-6 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16] max-w-5xl">
            {study.h1[0]} <span className="font-accent font-normal normal-case text-[#38BDF8] text-[1.1em]">{study.h1[1]}</span>
          </h1>
        </motion.div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 pb-24 md:pb-32 items-start">
        <div className="lg:col-span-6 space-y-8">
          <Reveal>
            <p className="font-accent text-2xl md:text-3xl text-[#2E1B16] leading-snug">{study.summary}</p>
          </Reveal>
          <dl className="divide-y divide-[#2E1B16]/10" data-testid="case-study-sections">
            {study.sections.map(([label, body]) => (
              <Reveal key={label}>
                <div className="grid sm:grid-cols-[10rem_1fr] gap-2 sm:gap-6 py-5">
                  <dt className="label-eyebrow text-[#2E1B16]/45 pt-1">{label}</dt>
                  <dd className="text-lg leading-relaxed text-[#2E1B16]/80">{body}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
          <Reveal>
            <div className="flex flex-wrap gap-4 pt-2">
              {study.instagram && (
                <a href={study.instagram} target="_blank" rel="noopener noreferrer" data-testid="case-study-instagram"
                  className="group inline-flex items-center gap-3 rounded-full border border-[#2E1B16]/20 px-6 py-3 font-semibold text-[#2E1B16] hover:border-[#38BDF8] hover:text-[#38BDF8] transition-colors">
                  <Instagram size={18} /> Visit {study.instagramHandle || "their Instagram"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              )}
              <Link to="/enquire" data-testid="case-study-cta" className="inline-flex items-center gap-2 rounded-full bg-[#2E1B16] text-[#FAF7F2] font-semibold px-6 py-3 hover:bg-[#38BDF8] hover:text-[#2E1B16] transition-colors">
                Tell us about your business <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>

        {study.reel && (
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal><InstagramEmbed url={study.reel} className="w-full" /></Reveal>
          </div>
        )}
      </section>

      {study.servicesUsed?.length > 0 && (
        <section className="bg-[#F2EFE9] py-20 md:py-28" aria-labelledby="case-services-heading">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-4"><h2 id="case-services-heading" className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16]">What we did</h2></Reveal>
            <ul className="lg:col-span-7 lg:col-start-6 divide-y divide-[#2E1B16]/10" data-testid="case-study-services">
              {study.servicesUsed.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="group flex items-center justify-between py-4 font-display text-xl md:text-2xl tracking-tight text-[#2E1B16] hover:text-[#38BDF8] transition-colors">
                    {label} <ArrowUpRight size={20} className="text-[#2E1B16]/30 group-hover:text-[#38BDF8] -rotate-45 group-hover:rotate-0 transition-all" />
                  </Link>
                </li>
              ))}
              {study.industryPath && (
                <li>
                  <Link to={study.industryPath} className="group flex items-center justify-between py-4 font-display text-xl md:text-2xl tracking-tight text-[#2E1B16] hover:text-[#38BDF8] transition-colors">
                    {study.industry} marketing <ArrowUpRight size={20} className="text-[#2E1B16]/30 group-hover:text-[#38BDF8] -rotate-45 group-hover:rotate-0 transition-all" />
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </section>
      )}

      <PersonalCTA />
      <Contact pageLabel={`Case study: ${study.client}`} />
    </article>
  );
};

export default CaseStudyPage;
